import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ShieldCheck, Check, Info } from "../components/icons.js";
import { api, ApiError } from "../lib/api.js";
import { AuthBrand } from "./Login.js";

/**
 * Set / reset password — one screen for two one-time links:
 *   /set-password?token=…   (account invite → first password)
 *   /reset-password?token=… (forgot-password → new password)
 * Both consume the same POST /auth/password/set {token, password}; only the copy differs. The
 * raw 64-hex token rides in the query string and is single-use server-side. No session is
 * minted here — on success we send the user to /login to sign in with their new password.
 */
export function SetPassword(): ReactNode {
  const [params] = useSearchParams();
  const location = useLocation();
  const token = params.get("token") ?? "";
  // /reset-password vs /set-password — same component, different framing.
  const isReset = location.pathname.startsWith("/reset");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [capsOn, setCapsOn] = useState(false);

  const submit = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>("/auth/password/set", { token, password }),
  });

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = token.length > 0 && password.length >= 8 && confirm === password && !submit.isPending;

  const title = isReset ? "Reset your password" : "Set your password";
  const subtitle = isReset
    ? "Choose a new password for your account."
    : "Welcome to TalentHop — choose a password to activate your account.";

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Use a password of at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    submit.mutate(undefined, {
      onError: (err) => {
        if (err instanceof ApiError && err.status === 400) {
          // Password-reuse is a distinct, retryable case — the link stays valid (QA #72).
          const details = (err.body as { details?: { code?: string } } | undefined)?.details;
          if (details?.code === "password_reuse") {
            setError("Your new password must be different from your current password.");
          } else {
            setError("This link is invalid or has expired. Request a new one to continue.");
          }
        } else {
          setError("Couldn't set your password just now. Please retry in a moment.");
        }
      },
    });
  };

  // Missing token — the link was mangled or opened without one. Fail early and point onward.
  if (!token) {
    return (
      <div className="auth-wrap t1-auth">
        <AuthBrand />
        <div className="auth-card">
          <div className="auth-head">
            <span className="mark">
              <ShieldCheck />
            </span>
            <h1>{title}</h1>
          </div>
          <div className="banner crit" style={{ marginBottom: 16 }}>
            <Info />
            <span>This link is invalid or has expired. Please use the most recent email we sent you.</span>
          </div>
          <p className="auth-hint">
            {isReset ? (
              <>
                Need a new link? <Link to="/forgot-password">Request a password reset</Link>
              </>
            ) : (
              <>
                Already have a password? <Link to="/login">Sign in</Link>
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  // Success — password saved; every old session was invalidated server-side. Route to sign-in.
  if (submit.isSuccess) {
    return (
      <div className="auth-wrap t1-auth">
        <AuthBrand />
        <div className="auth-card">
          <div className="auth-head">
            <span className="mark">
              <ShieldCheck />
            </span>
            <h1>{isReset ? "Password reset" : "Password set"}</h1>
          </div>
          <div className="banner info" style={{ marginBottom: 16 }}>
            <Check />
            <span>
              Your password is {isReset ? "updated" : "set"}. Sign in with your email and new password to continue.
            </span>
          </div>
          <Link
            to="/login"
            className="btn primary"
            style={{ width: "100%", justifyContent: "center" }}
          >
            Continue to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-wrap t1-auth">
        <AuthBrand />
      <form className="auth-card" onSubmit={onSubmit}>
        <div className="auth-head">
          <span className="mark">
            <ShieldCheck />
          </span>
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>

        {error && (
          <div className="banner crit" style={{ marginBottom: 16 }}>
            <Info />
            <span>{error}</span>
          </div>
        )}

        <div className="field">
          <label htmlFor="new-password">
            New password <span className="hint">— at least 8 characters</span>
          </label>
          <input
            id="new-password"
            className="input"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => setCapsOn(e.getModifierState("CapsLock"))}
            onKeyUp={(e) => setCapsOn(e.getModifierState("CapsLock"))}
            onBlur={() => setCapsOn(false)}
            placeholder="••••••••"
          />
          {capsOn && (
            <p className="auth-hint" role="status" style={{ marginTop: 6, textAlign: "left", color: "var(--warn)" }}>
              Caps Lock is on.
            </p>
          )}
          {tooShort && (
            <p className="auth-hint" style={{ textAlign: "left", marginTop: 6, color: "var(--crit)" }}>
              Use at least 8 characters.
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor="confirm-password">Confirm password</label>
          <input
            id="confirm-password"
            className="input"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onKeyDown={(e) => setCapsOn(e.getModifierState("CapsLock"))}
            onKeyUp={(e) => setCapsOn(e.getModifierState("CapsLock"))}
            onBlur={() => setCapsOn(false)}
            placeholder="••••••••"
          />
          {capsOn && (
            <p className="auth-hint" role="status" style={{ marginTop: 6, textAlign: "left", color: "var(--warn)" }}>
              Caps Lock is on.
            </p>
          )}
          {mismatch && (
            <p className="auth-hint" style={{ textAlign: "left", marginTop: 6, color: "var(--crit)" }}>
              Those passwords don't match.
            </p>
          )}
        </div>

        <button
          className="btn primary"
          type="submit"
          style={{ width: "100%", justifyContent: "center", marginTop: 4 }}
          disabled={!canSubmit}
        >
          {submit.isPending ? "Saving…" : isReset ? "Reset password" : "Set password"}
        </button>

        <p className="auth-hint" style={{ marginTop: 14 }}>
          Already have a password? <Link to="/login">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
